import { Dispatch, SetStateAction, useState } from 'react';
import { Icon, Tooltip } from '@iwoca/orion';
import { Trans, t } from '@lingui/macro';
import { ButtonLink } from 'components/ButtonLink/ButtonLink';
import { DateTimePicker } from 'components/DatePicker/DateTimePicker';
import { PhoneNumberInputModal } from 'components/PhoneNumberInputModal/PhoneNumberInputModal';
import {
  isWeekday,
  timeOptionIsEarlierThanCurrentTime,
  times,
} from 'components/ReviewOffer/ConfirmOffer/ContactTimes/ReviewOfferHelper';
import { subDays } from 'date-fns';
import { useAmplitude } from 'hooks/useAmplitude';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import styles from './ContactTimes.module.css';

type TProps = {
  timeout: string;
  originalPhoneNumber?: string;
  phoneNumberValue: string | undefined;
  setPhoneNumberValue: Dispatch<SetStateAction<string | undefined>>;
  setAsapTimeSelected: Dispatch<SetStateAction<boolean>>;
  setContactDateTime: Dispatch<SetStateAction<string | null>>;
};

export const ContactTimes = ({
  timeout,
  originalPhoneNumber,
  phoneNumberValue,
  setPhoneNumberValue,
  setContactDateTime,
  setAsapTimeSelected,
}: TProps) => {
  const { trackEvent } = useAmplitude();
  const timeoutDate = new Date(timeout);
  const [showPhoneNumberModal, setShowPhoneNumberModal] =
    useState<boolean>(false);

  // Format date with correct UTC timezone and producing the correct date when using toISOstring()
  // Issue was the date provided when converted to ISO string was the day before. Function below is to help offset that timezone difference
  const dateFormattingTimezone = (date: Date) => {
    if (date) {
      date.setHours((-1 * date.getTimezoneOffset()) / 60);
    }

    return date;
  };

  const handleDatePickerChange = (
    contactDate: Date | null,
    contactTime: string | undefined,
  ) => {
    if (!contactDate) return;

    if (contactTime !== 'asap') {
      const formattedDate = dateFormattingTimezone(contactDate)
        .toISOString()
        .split('T', 1)[0];
      const timestamp = `${formattedDate}T${contactTime}`;
      setAsapTimeSelected(false);
      return setContactDateTime(timestamp);
    }

    setContactDateTime(null);
    setAsapTimeSelected(true);
  };

  const isValidDate = (date: Date) => {
    const dayBefore = subDays(new Date(), 1);
    return isWeekday(date) && date >= dayBefore && timeoutDate >= date;
  };

  return (
    <div className={styles.contactInfo}>
      <div className={styles.headingContainer}>
        <h3 className={styles.heading}>
          <Trans>Applicant's contact details:</Trans>
        </h3>
        <Tooltip
          id="contact-date-info"
          content={t`Please select a date before the offer expires. We will try to contact them within this time.`}
        />
      </div>
      <div className={styles.contactDetails}>
        <p>
          <Trans>Contact date:</Trans>
        </p>
        <DateTimePicker
          showTimeInput
          timeOptions={times()}
          handleChange={handleDatePickerChange}
          filterDate={isValidDate}
          disableTimeOption={timeOptionIsEarlierThanCurrentTime}
        />
      </div>
      <div className={styles.contactDetails}>
        <p>
          <Trans>Contact number:</Trans>
        </p>
        <ButtonLink
          className={styles.phoneNumberLink}
          data-testid="phone-number-button"
          onClick={() => {
            trackEvent(AMPLITUDE_EVENTS.modalOpened, {
              'Page name': document.title,
              URL: window.location.pathname,
              'Modal name': 'Phone number input modal',
            });
            setShowPhoneNumberModal(true);
          }}
        >
          {phoneNumberValue ? phoneNumberValue : originalPhoneNumber}
          <Icon icon="penFilled" className={styles.inputIcon} />
        </ButtonLink>
      </div>
      <p className={styles.message}>
        <Trans>We'll contact the applicant once you confirm this offer.</Trans>
      </p>
      {showPhoneNumberModal && (
        <PhoneNumberInputModal
          showModal={showPhoneNumberModal}
          closeModal={() => setShowPhoneNumberModal(false)}
          setPhoneNumber={setPhoneNumberValue}
          phoneNumber={
            phoneNumberValue ? phoneNumberValue : originalPhoneNumber
          }
        />
      )}
    </div>
  );
};
